import React from "react";
import * as api from "../services/api";
import { currentLocationFromRequest } from "../components/LocationSelector";
import { useLoaderData, useRevalidator } from "react-router";
import { groupBy } from "shared/src/util.mjs";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { formData, FormErrorList, useFormAction } from "../form";
import ButtonLoader from "../components/ButtonLoader";
import TagsInput from "../components/TagsInput";
import { useRoles } from "../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { companyTagGroup } from "shared/src/client.mjs";

/**
 * @typedef Tag
 * @property {string} company_id
 * @property {string} group
 * @property {string} value
 */

export function loader({ request }) {
  const location_id = currentLocationFromRequest(request);

  return api.loadCompanyTags({ location_id }, api.contextFromRequest(request));
}

const defaultTags = {
  [companyTagGroup.IMPORTANT_INFO]: [],
  [companyTagGroup.LANGUAGES]: [],
  [companyTagGroup.CONTACT_TYPES]: [],
  [companyTagGroup.TREATMENT_STATE]: [],
};

function useTagsByGroup() {
  const loaderData = useLoaderData();

  return React.useMemo(() => {
    const tags = loaderData?.data?.company?.tags ?? [];

    return { ...defaultTags, ...groupBy(tags, (tag) => tag.group) };
  }, [loaderData]);
}

function Section({ title, children, onEdit }) {
  return (
    <>
      <Card display="flex" gap="1rem">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h2" gutterBottom>
              {title}
            </Typography>
            <Button
              onClick={onEdit}
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faPenToSquare} />}
            >
              Upravit
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              gap: "5rem",
            }}
          >
            <Box flexGrow="1">{children}</Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}

function formConverter(data) {
  return { values: [], ...data };
}

const tagRemovalRoles = new Set([
  role.Manager_Clinic,
  role.Manager_Customer_Care,
]);

function TagGroupEditDialog({ company_id, group, values, close }) {
  const roles = useRoles();
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.replaceCompanyTagGroup(formConverter(formData(e.target))),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open onClose={() => close()} maxWidth="md">
      <DialogTitle>{group}</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <input type="hidden" name="company_id" value={company_id} />
            <input type="hidden" name="group" value={group} />
            <TagsInput
              name="values"
              defaultValue={values}
              disableRemove={!isGrantedAny(roles, tagRemovalRoles)}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => close()}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function TagGroup({ company_id, group, tags }) {
  const [edit, setEdit] = React.useState(false);

  return (
    <>
      <Section onEdit={() => setEdit(true)}>
        <List subheader={group}>
          {tags.map((tag) => (
            <ListItem key={tag.value}>
              <ListItemText primary={tag.value} />
            </ListItem>
          ))}
        </List>
      </Section>
      {edit && (
        <TagGroupEditDialog
          company_id={company_id}
          group={group}
          values={tags.map((t) => t.value)}
          close={() => setEdit(false)}
          tags={tags}
        />
      )}
    </>
  );
}

export default function Settings() {
  const loaderData = useLoaderData();
  const company_id = loaderData?.data?.company?.id;
  const tagsByGroup = useTagsByGroup();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {Object.entries(tagsByGroup).map(([group, tags]) => (
        <TagGroup
          key={group}
          company_id={company_id}
          group={group}
          tags={tags}
        />
      ))}
    </Box>
  );
}
